import { useState, useEffect } from "react";
import { API } from "@/App";
import { usePageContent } from "@/hooks/usePageContent";
import usePageTitle from "@/hooks/usePageTitle";
import axios from "axios";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Calendar as CalendarIcon,
  Send,
  Clock,
  User,
  Building,
  MessageSquare,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fr } from "date-fns/locale";
import { format, addDays, isBefore, isWeekend } from "date-fns";

const MAUD_IMAGE = `${process.env.REACT_APP_BACKEND_URL}/api/files/alma-vivu/photos/contact-maud-phone.png`;

const timeSlots = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00"
];

const subjects = [
  "Organisation d'un atelier de sensibilisation",
  "Formation & montée en compétences",
  "Prise de renseignements"
];

const Contact = () => {
  const { getField } = usePageContent("contact");
  usePageTitle("Contact");
  const [activeTab, setActiveTab] = useState("contact");
  const [bookedSlots, setBookedSlots] = useState([]);
  
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: ""
  });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  const [appointmentForm, setAppointmentForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    message: ""
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [appointmentLoading, setAppointmentLoading] = useState(false);
  const [appointmentSuccess, setAppointmentSuccess] = useState(false);

  useEffect(() => {
    fetchBookedSlots();
  }, []);

  const fetchBookedSlots = async () => {
    try {
      const response = await axios.get(`${API}/appointments/booked-slots`);
      setBookedSlots(response.data);
    } catch (error) {
      console.error("Failed to fetch booked slots:", error);
    }
  };

  const isSlotBooked = (date, time) => {
    if (!date) return false;
    const dateStr = format(date, "yyyy-MM-dd");
    return bookedSlots.some(slot => slot.date === dateStr && slot.time === time);
  };

  const handleContactChange = (e) => {
    setContactForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleAppointmentChange = (e) => {
    setAppointmentForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);

    try {
      await axios.post(`${API}/contact`, contactForm);
      setContactSuccess(true);
      toast.success("Message envoyé avec succès !");
      setContactForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
      console.error(error);
    } finally {
      setContactLoading(false);
    }
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime) {
      toast.error("Veuillez sélectionner une date et un horaire");
      return;
    }

    setAppointmentLoading(true);

    try {
      await axios.post(`${API}/appointments`, {
        ...appointmentForm,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime
      });
      setAppointmentSuccess(true);
      toast.success("Rendez-vous demandé avec succès !");
      fetchBookedSlots();
    } catch (error) {
      toast.error("Erreur lors de la demande de rendez-vous");
      console.error(error);
    } finally {
      setAppointmentLoading(false);
    }
  };

  const disabledDays = (date) => {
    return isBefore(date, addDays(new Date(), 1)) || isWeekend(date);
  };

  return (
    <div className="min-h-screen bg-[var(--bone)]" data-testid="contact-page">
      {/* Hero Section */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        <div className="blob-bg w-[400px] h-[400px] bg-[var(--terracotta)]/15 top-0 right-0"></div>
        <div className="blob-bg w-[300px] h-[300px] bg-[var(--maquis)]/10 bottom-0 left-20"></div>

        <div className="container-premium relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl md:text-6xl font-bold text-[var(--charcoal)] mb-6 leading-tight">
              Parlons de votre <span className="text-[var(--terracotta)] emphasis">projet</span>
            </h1>
            <p className="text-xl text-[var(--stone)] leading-relaxed">
              Vous êtes un établissement scolaire (école primaire, collège, lycée, enseignement supérieur), un centre de formation et vous souhaitez organiser un atelier, former vos équipes éducatives ou concevoir un module sur mesure ? Contactez-moi.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-20">
        <div className="container-premium">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Contact Info */}
            <div className="lg:col-span-1 space-y-6">
              {/* Maud Card */}
              <div className="card-premium p-8" data-testid="maud-card">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-[var(--sand)]">
                    <img 
                      src={getField("maud", "image", MAUD_IMAGE)} 
                      alt="Maud - ALMA VIVU"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="font-display text-xl font-semibold text-[var(--charcoal)]">
                      {getField("maud", "name", "Maud")}
                    </h3>
                    <p className="text-sm text-[var(--stone)]">{getField("maud", "title", "Fondatrice ALMA VIVU")}</p>
                  </div>
                </div>
                <p className="text-[var(--stone)] text-sm leading-relaxed">
                  J'ai à cœur de transmettre des savoirs, d'écouter, de partager en réel (pas derrière un écran) de manière positive, engageante et surtout sans faire culpabiliser.
                </p>
              </div>

              {/* Contact Info */}
              <div className="card-premium p-8 space-y-6">
                <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--stone)]">
                  Coordonnées
                </h3>
                
                <a 
                  href="tel:0751131092" 
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-[var(--sand)] transition-colors"
                  data-testid="phone-link"
                >
                  <div className="w-12 h-12 bg-[var(--terracotta)]/10 rounded-xl flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[var(--terracotta)]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[var(--stone)]">Téléphone</p>
                    <p className="font-medium text-[var(--charcoal)]">07 51 13 10 92</p>
                  </div>
                </a>

                <a 
                  href="mailto:maud.almavivu@gmail.com" 
                  className="flex items-center gap-4 p-3 rounded-xl hover:bg-[var(--sand)] transition-colors"
                >
                  <div className="w-12 h-12 bg-[var(--maquis)]/10 rounded-xl flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[var(--maquis)]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[var(--stone)]">Email</p>
                    <p className="font-medium text-[var(--charcoal)]">maud.almavivu@gmail.com</p>
                  </div>
                </a>

                <div className="flex items-start gap-4 p-3">
                  <div className="w-12 h-12 bg-[var(--dusty-rose)]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[var(--terracotta-dark)]" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[var(--stone)]">Zone d'intervention</p>
                    <p className="font-medium text-[var(--charcoal)]">
                      Basée à Ajaccio, j'interviens uniquement et exclusivement sur toute la Corse
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Forms */}
            <div className="lg:col-span-2">
              <div className="card-premium overflow-hidden">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full border-b border-[var(--sand)] p-0 h-auto bg-transparent">
                    <TabsTrigger 
                      value="contact" 
                      className="flex-1 py-5 rounded-none data-[state=active]:bg-[var(--sand)] data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-[var(--terracotta)]"
                      data-testid="tab-contact"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Envoyer un message
                    </TabsTrigger>
                    <TabsTrigger 
                      value="appointment" 
                      className="flex-1 py-5 rounded-none data-[state=active]:bg-[var(--sand)] data-[state=active]:shadow-none border-b-2 border-transparent data-[state=active]:border-[var(--terracotta)]"
                      data-testid="tab-appointment"
                      onClick={(e) => {
                        e.preventDefault();
                        window.open('https://calendly.com/maud-almavivu/30min', '_blank');
                      }}
                    >
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      Prendre rendez-vous
                    </TabsTrigger>
                  </TabsList>

                  {/* Contact Form */}
                  <TabsContent value="contact" className="p-8 md:p-10">
                    {contactSuccess ? (
                      <div className="text-center py-16" data-testid="contact-success">
                        <div className="w-20 h-20 bg-[var(--maquis)]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                          <CheckCircle className="w-10 h-10 text-[var(--maquis)]" />
                        </div>
                        <h3 className="font-display text-2xl font-bold text-[var(--charcoal)] mb-3">
                          Message envoyé !
                        </h3>
                        <p className="text-[var(--stone)] mb-8">
                          Merci pour votre message. Nous vous répondrons rapidement.
                        </p>
                        <button 
                          onClick={() => setContactSuccess(false)}
                          className="btn-secondary"
                        >
                          Envoyer un autre message
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label htmlFor="contact-name" className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                              Nom complet *
                            </label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--stone)]" />
                              <input
                                id="contact-name"
                                name="name"
                                value={contactForm.name}
                                onChange={handleContactChange}
                                placeholder="Votre nom"
                                className="input-premium pl-12"
                                required
                                data-testid="contact-name-input"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label htmlFor="contact-email" className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                              Email *
                            </label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--stone)]" />
                              <input
                                id="contact-email"
                                name="email"
                                type="email"
                                value={contactForm.email}
                                onChange={handleContactChange}
                                placeholder="votre@email.com"
                                className="input-premium pl-12"
                                required
                                data-testid="contact-email-input"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <label htmlFor="contact-phone" className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                              Téléphone
                            </label>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--stone)]" />
                              <input
                                id="contact-phone"
                                name="phone"
                                value={contactForm.phone}
                                onChange={handleContactChange}
                                placeholder="Votre numéro"
                                className="input-premium pl-12"
                                data-testid="contact-phone-input"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label htmlFor="contact-company" className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                              Structure / Établissement / Centre de formation
                            </label>
                            <div className="relative">
                              <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--stone)]" />
                              <input
                                id="contact-company"
                                name="company"
                                value={contactForm.company}
                                onChange={handleContactChange}
                                placeholder="Votre structure"
                                className="input-premium pl-12"
                                data-testid="contact-company-input"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label htmlFor="contact-subject" className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                            Votre besoin *
                          </label>
                          <select
                            id="contact-subject"
                            name="subject"
                            value={contactForm.subject}
                            onChange={handleContactChange}
                            className="input-premium w-full bg-transparent"
                            required
                            data-testid="contact-subject-select"
                          >
                            <option value="">Sélectionnez votre besoin</option>
                            {subjects.map((subject, index) => (
                              <option key={index} value={subject}>{subject}</option>
                            ))}
                          </select>
                        </div>

                        <div className="space-y-2">
                          <label htmlFor="contact-message" className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                            Message *
                          </label>
                          <textarea
                            id="contact-message"
                            name="message"
                            value={contactForm.message}
                            onChange={handleContactChange}
                            placeholder="Précisez votre besoin avec votre cible, votre lieu géographique, votre délai, vos attentes..."
                            className="input-premium min-h-[150px] resize-none"
                            required
                            data-testid="contact-message-input"
                          />
                        </div>

                        <button 
                          type="submit" 
                          className="btn-primary w-full flex items-center justify-center gap-2"
                          disabled={contactLoading}
                          data-testid="contact-submit-btn"
                        >
                          {contactLoading ? (
                            <div className="w-5 h-5 border-2 border-[var(--bone)] border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              Envoyer le message
                              <Send className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      </form>
                    )}
                  </TabsContent>

                  {/* Appointment Form */}
                  <TabsContent value="appointment" className="p-8 md:p-10">
                    {appointmentSuccess ? (
                      <div className="text-center py-16" data-testid="appointment-success">
                        <div className="w-20 h-20 bg-[var(--maquis)]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                          <CheckCircle className="w-10 h-10 text-[var(--maquis)]" />
                        </div>
                        <h3 className="font-display text-2xl font-bold text-[var(--charcoal)] mb-3">
                          Demande envoyée !
                        </h3>
                        <p className="text-[var(--terracotta)] font-medium mb-2">
                          {selectedDate && format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })} à {selectedTime}
                        </p>
                        <p className="text-[var(--stone)] mb-8">
                          Maud vous contactera pour confirmer ce créneau.
                        </p>
                        <button 
                          onClick={() => {
                            setAppointmentSuccess(false);
                            setSelectedDate(null);
                            setSelectedTime(null);
                            setAppointmentForm({
                              name: "",
                              email: "",
                              phone: "",
                              company: "",
                              subject: "",
                              message: ""
                            });
                          }}
                          className="btn-secondary"
                        >
                          Prendre un autre rendez-vous
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleAppointmentSubmit} className="space-y-8" data-testid="appointment-form">
                        {/* Date Selection */}
                        <div className="grid lg:grid-cols-2 gap-8">
                          <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[var(--stone)] mb-4 block">
                              Choisissez une date *
                            </label>
                            <div className="bg-[var(--sand)] rounded-2xl p-4">
                              <Calendar
                                mode="single"
                                selected={selectedDate}
                                onSelect={setSelectedDate}
                                disabled={disabledDays}
                                locale={fr}
                                className="rounded-md"
                                data-testid="appointment-calendar"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="text-xs uppercase tracking-wider font-bold text-[var(--stone)] mb-4 block">
                              Choisissez un horaire *
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                              {timeSlots.map((time) => {
                                const isBooked = isSlotBooked(selectedDate, time);
                                return (
                                  <button
                                    key={time}
                                    type="button"
                                    onClick={() => !isBooked && setSelectedTime(time)}
                                    disabled={isBooked || !selectedDate}
                                    data-testid={`time-slot-${time}`}
                                    className={`p-3 rounded-xl text-sm font-medium transition-all ${
                                      selectedTime === time
                                        ? 'bg-[var(--terracotta)] text-[var(--bone)]'
                                        : isBooked
                                          ? 'bg-[var(--sand)] text-[var(--stone)]/40 cursor-not-allowed'
                                          : !selectedDate
                                            ? 'bg-[var(--sand)] text-[var(--stone)]/40 cursor-not-allowed'
                                            : 'bg-[var(--sand)] text-[var(--charcoal)] hover:bg-[var(--sand-dark)]'
                                    }`}
                                  >
                                    <Clock className="w-4 h-4 mx-auto mb-1" />
                                    {time}
                                  </button>
                                );
                              })}
                            </div>
                            {selectedDate && selectedTime && (
                              <p className="mt-6 text-sm text-[var(--terracotta)] font-medium">
                                Sélectionné : {format(selectedDate, "EEEE d MMMM", { locale: fr })} à {selectedTime}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Personal Info */}
                        <div className="border-t border-[var(--sand)] pt-8">
                          <h4 className="text-xs uppercase tracking-wider font-bold text-[var(--stone)] mb-6">
                            Vos informations
                          </h4>
                          
                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                              <label className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                                Nom complet *
                              </label>
                              <input
                                name="name"
                                value={appointmentForm.name}
                                onChange={handleAppointmentChange}
                                placeholder="Votre nom"
                                className="input-premium"
                                required
                                data-testid="apt-name-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                                Email *
                              </label>
                              <input
                                name="email"
                                type="email"
                                value={appointmentForm.email}
                                onChange={handleAppointmentChange}
                                placeholder="votre@email.com"
                                className="input-premium"
                                required
                                data-testid="apt-email-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                                Téléphone *
                              </label>
                              <input
                                name="phone"
                                value={appointmentForm.phone}
                                onChange={handleAppointmentChange}
                                placeholder="Votre numéro"
                                className="input-premium"
                                required
                                data-testid="apt-phone-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                                Structure / Établissement
                              </label>
                              <input
                                name="company"
                                value={appointmentForm.company}
                                onChange={handleAppointmentChange}
                                placeholder="Votre structure"
                                className="input-premium"
                                data-testid="apt-company-input"
                              />
                            </div>
                          </div>

                          <div className="space-y-2 mt-6">
                            <label className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                              Sujet du rendez-vous *
                            </label>
                            <select
                              name="subject"
                              value={appointmentForm.subject}
                              onChange={handleAppointmentChange}
                              className="input-premium w-full bg-transparent"
                              required
                              data-testid="apt-subject-select"
                            >
                              <option value="">Sélectionnez un sujet</option>
                              {subjects.map((subject, index) => (
                                <option key={index} value={subject}>{subject}</option>
                              ))}
                            </select>
                          </div>

                          <div className="space-y-2 mt-6">
                            <label className="text-xs uppercase tracking-wider font-bold text-[var(--stone)]">
                              Message (optionnel)
                            </label>
                            <textarea
                              name="message"
                              value={appointmentForm.message}
                              onChange={handleAppointmentChange}
                              placeholder="Des précisions sur votre demande..."
                              className="input-premium min-h-[100px] resize-none"
                              data-testid="apt-message-input"
                            />
                          </div>
                        </div>

                        <button 
                          type="submit" 
                          className="btn-primary w-full flex items-center justify-center gap-2"
                          disabled={appointmentLoading || !selectedDate || !selectedTime}
                          data-testid="apt-submit-btn"
                        >
                          {appointmentLoading ? (
                            <div className="w-5 h-5 border-2 border-[var(--bone)] border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              Demander ce rendez-vous
                              <CalendarIcon className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      </form>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
